import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { PasswordInput } from '@aqarmap/aqarmap-ui';
import LayoutSkeleton from '~/components/LayoutSkeleton';
import ErrorMessage from '~/components/ErrorMessage';
import useFormValidations from '~/hooks/useFormValidations';
import {
  useGetUserProfileQuery,
  useChangeUserPhoneMutation,
} from '~/redux/api/authApiSlice';
import { objectSerialize } from '~/lib';
import { openModal } from '~/redux/features/uiState/uiStateSlice';
import { useAppDispatch } from '~/redux/hooks';
import PhoneInputController from '~/components/PhoneInputController';
import OtpModal from '~/components/OtpModal';
import PageLayout from '~myAccount/components/PageLayout';
import FormActions from '~myAccount/components/FormActions';
import { useFullRoutes } from '~/hooks';

const PhoneUpdate = () => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation('app');
  const locale = i18n.language;
  const [errorMessage, setErrorMessage] = useState('');
  const { phoneSchema, passwordSchema } = useFormValidations();
  const { MY_ACCOUNT_URL } = useFullRoutes();

  const [changeUserPhone, { isLoading: updatePhoneNumberIsLoading, isError }] =
    useChangeUserPhoneMutation();

  const {
    data: userProfile,
    isLoading: userIsLoading,
    isSuccess,
  } = useGetUserProfileQuery();

  const updatePhoneSchema = z.object({
    phone: phoneSchema.refine(
      (val) => {
        if (!userProfile?.phone) return true;
        return userProfile.phone !== val;
      },
      { message: t('phoneNotChanged') },
    ),
    currentPassword: passwordSchema,
  });

  type IUpdatePhone = z.infer<typeof updatePhoneSchema>;

  const { control, handleSubmit, getValues } = useForm<IUpdatePhone>({
    resolver: zodResolver(updatePhoneSchema),
    mode: 'onSubmit',
    values: { phone: userProfile?.phone ?? '', currentPassword: '' },
  });

  const onSubmit = async ({ currentPassword, phone }: IUpdatePhone) => {
    const formData = objectSerialize({
      temporaryPhone: phone,
      currentPassword,
    });
    const res: any = await changeUserPhone(formData);
    if (res.data && !res.error) {
      dispatch(openModal('otpModal'));
    }
    if (res.error) {
      setErrorMessage(res.error.data?.message || t('failedChangePhoneNumber'));
    }
  };

  return (
    <PageLayout
      title={t('privacy')}
      bodyClassName="border border-gray rounded-[8px]"
    >
      {userIsLoading && <LayoutSkeleton />}
      {!userIsLoading && isSuccess && (
        <div className="py-3x">
          {isError && <ErrorMessage message={errorMessage} />}
          <h2 className="mb-2x text-title_4 px-3x">{t('phoneNumber')}</h2>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-3x px-3x pb-3x">
              <PhoneInputController
                control={control}
                field={{
                  placeholder: t('phoneNumber'),
                  autoFocus: true,
                  name: 'phone',
                }}
                label={{
                  text: t('phoneNumber'),
                  className: 'font-[600] block',
                }}
              />
              <Controller
                control={control}
                name="currentPassword"
                render={({ field, fieldState: { error } }) => (
                  <div className="flex flex-col gap-1.5x">
                    <PasswordInput
                      field={{
                        ...field,
                        placeholder: t('currentPassword'),
                        id: 'currentPassword',
                      }}
                      error={error?.message}
                      label={{
                        text: t('currentPassword'),
                        className: 'font-[600] block',
                      }}
                    />
                    <a
                      href={`${process.env.REACT_APP_AUTH_BASE_URL}/${locale}/reset-password`}
                      className="text-link-1 text-blue text-end"
                    >
                      {t('forgetPassword')}
                    </a>
                  </div>
                )}
              />
            </div>

            <FormActions
              isLoading={updatePhoneNumberIsLoading}
              disabled={updatePhoneNumberIsLoading}
            />
          </form>
        </div>
      )}
      <OtpModal
        title={t('updatePhoneNumber')}
        description={
          <p className="text-body_1 text-gray__dark_1">
            <span className="font-bold text-title_4">
              {t('forUpdatePhoneNumber')}
            </span>
            {t('enterUpdateNumberOtp')}
          </p>
        }
        phone={getValues('phone')}
        redirectPage={MY_ACCOUNT_URL.ROOT}
      />
    </PageLayout>
  );
};

export default PhoneUpdate;
